import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Button } from '@/components/ui/button';
import { Unlink } from 'lucide-react';
import { useState } from 'react';
import { useDisconnectMetaIntegration } from '../api';

interface UnlinkMetaButtonProps {
  /** The Facebook profile the login used, when Meta told us. */
  facebookUserName?: string | null;
  /** Fires once the connection is removed. */
  onUnlinked?: () => void;
  disabled?: boolean;
}

/**
 * The way out of the connect funnel for a Facebook login that should not be
 * here: the wrong personal profile, or one that reaches no ad account or Page.
 * Without it the owner can only reconnect the same login or leave the
 * connection half-finished, and support has to unlink it by hand.
 *
 * Unlinking is the same disconnect as the Integrations card: the token is
 * dropped, saved work is kept, and a reconnect starts from a fresh login.
 */
export function UnlinkMetaButton({
  facebookUserName,
  onUnlinked,
  disabled,
}: UnlinkMetaButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { disconnectAsync, isDisconnecting } = useDisconnectMetaIntegration();

  const handleConfirm = async () => {
    try {
      await disconnectAsync();
      onUnlinked?.();
    } catch {
      // Error toast is handled by the hook.
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        disabled={disabled || isDisconnecting}
        onClick={() => setConfirmOpen(true)}
      >
        <Unlink className="size-4" />
        Unlink Facebook
      </Button>

      <ConfirmDeleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Unlink Facebook?"
        description={`${
          facebookUserName ? `You're connected as ${facebookUserName}. ` : ''
        }Unlinking removes this Facebook login from your workspace so you can connect a different account. Nothing is posted or changed on Facebook.`}
        confirmLabel="Unlink Facebook"
        icon={Unlink}
        isPending={isDisconnecting}
        onConfirm={() => void handleConfirm()}
      />
    </>
  );
}
